import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import AvailableImageItem from '~/components/ImageItem/AvailableImageItem';
import { StructureSet } from '~/domain/patient/structureset';
import { ImportResult } from '~/domain/services/dicomProcessor';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useSecondaryImage from '~/hooks/image/useSecondaryImage';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import ReformatDialog, { ReformatTarget } from '../ReformatDialog';

type Role = 'primary' | 'secondary' | null;

interface ImageDeleteTarget {
    image: AvailableImage;
    affectedSets: StructureSet[];
}

const ImageCard = React.memo(function ImageCard({
    image,
    role,
    isSelected,
    onSelect,
    onReformat,
    onDelete,
    onDownload,
}: {
    image: AvailableImage;
    role: Role;
    isSelected?: boolean;
    onSelect?: () => void;
    onReformat?: (img: AvailableImage) => void;
    onDelete?: (img: AvailableImage) => void;
    onDownload?: (img: AvailableImage) => void;
}) {
    const intl = useIntl();
    const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

    const isReformat = !!image.needsReformat;
    const showReformat = isSelected && onReformat;

    const handleReformatClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onReformat?.(image);
        },
        [onReformat, image]
    );

    return (
        <Box
            onClick={onSelect}
            sx={{
                position: 'relative',
                cursor: onSelect ? 'pointer' : 'default',
                bgcolor: isSelected
                    ? 'rgba(144, 202, 249, 0.08)'
                    : 'transparent',
                '&:hover': onSelect
                    ? {
                          bgcolor: isSelected
                              ? 'rgba(144, 202, 249, 0.12)'
                              : 'rgba(144, 202, 249, 0.06)',
                      }
                    : undefined,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'background-color 0.15s ease-in-out',
                    zIndex: 1,
                },
                transition: 'all 0.15s ease-in-out',
            }}
        >
            <AvailableImageItem
                image={image}
                isSecondary={role === 'secondary'}
                isSelected={false}
                onReformat={onReformat ? () => onReformat(image) : undefined}
                onDownload={onDownload ? () => onDownload(image) : undefined}
                onDelete={onDelete ? () => onDelete(image) : undefined}
            />

            {showReformat && (
                <Box
                    display="flex"
                    gap={0.5}
                    px={1.5}
                    pb={0.75}
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    {isReformat && (
                        <Tooltip title={t('reformatTooltip')}>
                            <Button
                                data-testid="reformat-image-button"
                                variant="contained"
                                color="warning"
                                size="small"
                                onClick={handleReformatClick}
                                sx={{ fontSize: '0.65rem' }}
                            >
                                <FormattedMessage id="reformatImage" />
                            </Button>
                        </Tooltip>
                    )}
                    {!isReformat && image.imageObject && (
                        <Tooltip title={t('reformatTooltip')}>
                            <Button
                                data-testid="reformat-loaded-image-button"
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleReformatClick}
                                sx={{ fontSize: '0.65rem' }}
                            >
                                <FormattedMessage id="reformatLoadedImage" />
                            </Button>
                        </Tooltip>
                    )}
                </Box>
            )}
        </Box>
    );
});

export default function ImageList() {
    const intl = useIntl();
    const { availableImages, primaryImage, handleChangePrimaryImage } =
        usePrimaryImage(true);
    const { selectedSecondaryImage } = useSecondaryImage();
    const { initializeEnvironment } = useViewportEnvironment();
    const { deleteImage, deleteRawSeries, getAffectedStructureSets } =
        useDeleteDataset();

    const [reformatTarget, setReformatTarget] = useState<ReformatTarget | null>(
        null
    );
    const [deleteTarget, setDeleteTarget] = useState<ImageDeleteTarget | null>(
        null
    );

    const getRole = useCallback(
        (image: AvailableImage): Role => {
            if (image.seriesInstanceUID === primaryImage?.seriesUid) {
                return 'primary';
            }
            if (
                image.imageObject?.dicomUid === selectedSecondaryImage?.dicomUid
            ) {
                return 'secondary';
            }
            return null;
        },
        [primaryImage?.seriesUid, selectedSecondaryImage?.dicomUid]
    );

    const handleImageClick = useCallback(
        (image: AvailableImage) => {
            handleChangePrimaryImage(image);
        },
        [handleChangePrimaryImage]
    );

    const handleOpenDelete = useCallback(
        (image: AvailableImage) => {
            if (!image.imageObject && !image.rawSeries) {
                return;
            }
            const affectedSets = image.imageObject
                ? getAffectedStructureSets(image.imageObject)
                : [];
            setDeleteTarget({ image, affectedSets });
        },
        [getAffectedStructureSets]
    );

    const handleConfirmDelete = useCallback(() => {
        if (!deleteTarget) {
            return;
        }
        const { image } = deleteTarget;
        if (image.imageObject) {
            deleteImage(image.imageObject);
        } else if (image.rawSeries) {
            deleteRawSeries(image.rawSeries);
        }
        setDeleteTarget(null);
    }, [deleteTarget, deleteImage, deleteRawSeries]);

    const handleOpenReformat = useCallback((image: AvailableImage) => {
        if (image.rawSeries) {
            setReformatTarget({ kind: 'rawSeries', series: image.rawSeries });
        } else if (image.imageObject) {
            setReformatTarget({
                kind: 'loadedImage',
                image: image.imageObject,
            });
        }
    }, []);

    const handleCloseReformat = useCallback(() => {
        setReformatTarget(null);
    }, []);

    const handleReformatComplete = useCallback(
        (importResult: ImportResult) => {
            initializeEnvironment(importResult);
            setReformatTarget(null);
        },
        [initializeEnvironment]
    );

    const deleteDialogMessage = useMemo(() => {
        if (!deleteTarget) {
            return '';
        }
        const base = intl.formatMessage({ id: 'deleteImageConfirm' });
        if (deleteTarget.affectedSets.length === 0) {
            return base;
        }
        const names = deleteTarget.affectedSets
            .map((ss) => ss.dicomStructureSet.structureSetLabel)
            .join(', ');
        return `${base} ${intl.formatMessage(
            { id: 'deleteImageStructureSetWarning' },
            { structureSets: names }
        )}`;
    }, [deleteTarget, intl]);

    if (availableImages.length === 0) {
        return null;
    }

    return (
        <>
            <Box>
                <Box px={1.5} py={1}>
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="images" />
                    </Typography>
                </Box>
                <Box>
                    {availableImages.map((img, index) => {
                        const role = getRole(img);
                        const isPrimary = role === 'primary';
                        return (
                            <React.Fragment key={img.seriesInstanceUID}>
                                {index > 0 && <Divider />}
                                <ImageCard
                                    image={img}
                                    role={role}
                                    isSelected={isPrimary}
                                    onSelect={
                                        !img.needsReformat
                                            ? () => handleImageClick(img)
                                            : undefined
                                    }
                                    onReformat={handleOpenReformat}
                                    onDelete={
                                        img.imageObject || img.rawSeries
                                            ? handleOpenDelete
                                            : undefined
                                    }
                                    onDownload={
                                        !img.imageObject && !img.rawSeries
                                            ? handleImageClick
                                            : undefined
                                    }
                                />
                            </React.Fragment>
                        );
                    })}
                </Box>
            </Box>

            <ConfirmationDialog
                open={deleteTarget !== null}
                title={intl.formatMessage({ id: 'deleteImage' })}
                message={deleteDialogMessage}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
                mode="error"
            />

            {reformatTarget && (
                <ReformatDialog
                    open
                    onClose={handleCloseReformat}
                    target={reformatTarget}
                    onReformatComplete={handleReformatComplete}
                />
            )}
        </>
    );
}
